import { useEffect, useState } from 'react';
import { confirmDialog } from '../../components/ui/confirm';
import { Badge } from '../../components/Badge';
import {
  DistributionLists,
  Users,
  type DistributionList,
  type DistMemberInput,
  type UserSummary,
} from '../../lib/api';
import { UserPicker } from '../../components/UserPicker';

export function DistributionListsAdminPage() {
  const [lists, setLists] = useState<DistributionList[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DistributionList | 'new' | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [l, u] = await Promise.all([DistributionLists.list(), Users.list()]);
      setLists(l.lists);
      setUsers(u.users);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function handleDelete(list: DistributionList) {
    const ok = await confirmDialog({
      title: 'Delete list?',
      body: `"${list.name}" will be removed and disconnected from any tickets that referenced it.`,
      tone: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await DistributionLists.remove(list.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-slate-900">Distribution lists</h1>
        <button
          onClick={() => setEditing('new')}
          className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700"
        >
          + New list
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <p className="text-sm text-slate-500">
        The list marked <strong>Default</strong> is automatically attached to every new ticket — so the New Ticket form doesn't have to ask. Only one list can be default.
      </p>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : lists.length === 0 ? (
        <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-md p-4">
          No distribution lists yet. Create one to start notifying groups on ticket events.
        </p>
      ) : (
        <ul className="space-y-2">
          {lists.map((l) => (
            <li key={l.id} className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap gap-3 items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 flex items-center gap-2">
                  {l.name}
                  {l.isDefault && <Badge tone="warn">Default</Badge>}
                </div>
                {l.description && (
                  <div className="text-sm text-slate-500 mt-0.5">{l.description}</div>
                )}
                <div className="text-xs text-slate-500 mt-1">
                  {l.members.length} {l.members.length === 1 ? 'member' : 'members'}
                  {l.members.length > 0 && (
                    <span className="ml-1 text-slate-500">
                      ({l.members.slice(0, 3).map((m) => m.user?.email ?? m.email).filter(Boolean).join(', ')}
                      {l.members.length > 3 && ` +${l.members.length - 3} more`})
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-3 text-sm items-center">
                {!l.isDefault && (
                  <button
                    onClick={async () => {
                      try {
                        await DistributionLists.update(l.id, { isDefault: true });
                        await load();
                      } catch (e) {
                        setError(e instanceof Error ? e.message : 'Failed to set default');
                      }
                    }}
                    className="text-xs text-amber-700 hover:text-amber-900"
                  >
                    Make default
                  </button>
                )}
                <button onClick={() => setEditing(l)} className="text-brand-600 hover:text-brand-700">Edit</button>
                <button onClick={() => handleDelete(l)} className="text-red-600 hover:text-red-700">Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <ListEditor
          users={users}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function ListEditor({
  users,
  initial,
  onClose,
  onSaved,
}: {
  users: UserSummary[];
  initial: DistributionList | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [members, setMembers] = useState<DistMemberInput[]>(
    initial?.members.map((m) => ({ userId: m.userId, email: m.email })) ?? [],
  );
  const [emailInput, setEmailInput] = useState('');
  const [userPick, setUserPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addUserMember() {
    if (!userPick) return;
    if (members.some((m) => m.userId === userPick)) return;
    setMembers([...members, { userId: userPick, email: null }]);
    setUserPick(null);
  }
  function addEmailMember() {
    const e = emailInput.trim();
    if (!e || !/^[^@\s]+@[^@\s]+$/.test(e)) {
      setError('Enter a valid email');
      return;
    }
    if (members.some((m) => m.email === e)) {
      setEmailInput('');
      return;
    }
    setMembers([...members, { userId: null, email: e }]);
    setEmailInput('');
    setError(null);
  }
  function removeMember(idx: number) {
    setMembers(members.filter((_, i) => i !== idx));
  }

  async function save() {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (initial) {
        await DistributionLists.update(initial.id, {
          name: name.trim(),
          description: description.trim() || null,
          isDefault,
          members,
        });
      } else {
        await DistributionLists.create({
          name: name.trim(),
          description: description.trim() || undefined,
          isDefault,
          members,
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-30">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {initial ? `Edit "${initial.name}"` : 'New distribution list'}
          </h2>
        </div>
        <div className="p-4 space-y-4">
          <label className="block">
            <span className="block text-xs text-slate-500 mb-1">Name *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className="input" autoFocus />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-500 mb-1">Description</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)} className="input" />
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-2 bg-amber-50 border border-amber-200 rounded">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium text-amber-900">Default list</span>
              <span className="block text-xs text-amber-800">Auto-attached to every new ticket so the creator doesn't have to pick. Only one list can be default.</span>
            </span>
          </label>

          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Members ({members.length})</h3>
            {members.length === 0 ? (
              <p className="text-sm text-slate-500">No members yet.</p>
            ) : (
              <ul className="space-y-1 mb-3">
                {members.map((m, i) => {
                  const u = m.userId ? users.find((x) => x.id === m.userId) : null;
                  return (
                    <li key={i} className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1">
                      <span>
                        {u ? (
                          <>
                            <span className="text-slate-900">{u.name}</span>{' '}
                            <span className="text-slate-500">&lt;{u.email}&gt;</span>
                          </>
                        ) : (
                          <span className="text-slate-900">{m.email}</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeMember(i)}
                        className="text-slate-500 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <UserPicker
                    users={users.filter((u) => !members.some((m) => m.userId === u.id))}
                    value={userPick}
                    onChange={setUserPick}
                    placeholder="Pick internal user…"
                  />
                </div>
                <button
                  type="button"
                  onClick={addUserMember}
                  disabled={!userPick}
                  className="px-3 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmailMember())}
                  placeholder="…or add an external email"
                  className="input"
                />
                <button
                  type="button"
                  onClick={addEmailMember}
                  className="px-3 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
